/**
 * Formátování pro UI. Peníze jsou v celém systému decimal STRINGY (invariant 3
 * — žádný float); formátování je čistě textové, bez převodu na number.
 */

/** „1240500.50" → „1 240 500,50 Kč". Null/undefined → „?" (nikdy 0 místo neznámé). */
export function formatKc(castka: string | null | undefined): string {
  if (castka === null || castka === undefined || castka === '') return '?';
  const zaporne = castka.startsWith('-');
  const [cela = '0', des] = (zaporne ? castka.slice(1) : castka).split('.');
  const skupiny = cela.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
  const desetiny = des !== undefined && des !== '' && !/^0+$/.test(des) ? `,${des}` : '';
  return `${zaporne ? '−' : ''}${skupiny}${desetiny}\u00a0Kč`;
}

/** Formát procenta jako text: „5" → „5 %", „2.5" → „2,5 %". */
export function formatProcento(procento: string | null | undefined): string {
  if (procento === null || procento === undefined || procento === '') return '?';
  return `${procento.replace('.', ',')}\u00a0%`;
}

/** Český zápis dne „d. m. rrrr" (TOP 10 — všude stejně, bez závislosti na locale OS). */
function ceskyDen(den: number, mesic: number, rok: number): string {
  return `${den}. ${mesic}. ${rok}`;
}

/** ISO datum → české datum „d. m. rrrr"; null → „—". */
export function formatDatum(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : ceskyDen(d.getDate(), d.getMonth() + 1, d.getFullYear());
}

/**
 * Kalendářní den (sloupce @db.Date — API posílá UTC půlnoc): formátuje se
 * v UTC, jinak by se den v časových pásmech za UTC posunul o den zpět.
 */
export function formatDen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : ceskyDen(d.getUTCDate(), d.getUTCMonth() + 1, d.getUTCFullYear());
}

/** Decimal string → celé haléře (BigInt) — peníze nikdy jako float (invariant 3). */
function naHalere(s: string): bigint {
  const zaporne = s.startsWith('-');
  const [cela = '0', des = ''] = (zaporne ? s.slice(1) : s).split('.');
  const halere = BigInt(cela) * 100n + BigInt((des + '00').slice(0, 2));
  return zaporne ? -halere : halere;
}

/** Haléře (BigInt) → decimal string. */
function zHaleru(halere: bigint): string {
  const abs = halere < 0n ? -halere : halere;
  const cela = abs / 100n;
  const des = (abs % 100n).toString().padStart(2, '0');
  return `${halere < 0n ? '-' : ''}${cela}${des === '00' ? '' : `.${des}`}`;
}

/**
 * Rozdíl dvou decimal stringů (a − b) bez floatu — přes celé haléře (BigInt).
 * Pro kontrolní součet importu vs. cena SOD.
 */
export function rozdilCastek(a: string, b: string): string {
  return zHaleru(naHalere(a) - naHalere(b));
}

/**
 * Součet decimal stringů bez floatu (Σ vybraných ZL do dodatku). Chybějící
 * hodnoty (zeď) se přeskakují — informativní součet, závazný počítá server.
 */
export function souctiCastky(castky: (string | null | undefined)[]): string {
  return zHaleru(castky.reduce((s, c) => (c ? s + naHalere(c) : s), 0n));
}
